// A place's data for the map's place card (PlaceCard.js) and a shared place on the area page (AreaPage.js):
// the homes sold at a street address (GET /price-history/address), and the words both use for them.
import { apiFetch } from './edgePayloads.js';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu';
const NBSP = ' ';

// "Sveavägen 120" from "Sveavägen 120, Stockholm"; '' for a street without a house number (no sales of its own).
export const streetAddress = (text) => {
    const first = String(text || '').split(',')[0].trim();
    return /^\D+\s+\d{1,4}\s*[A-Za-z]?$/.test(first) ? first : '';
};

const salesCache = new Map();

// The sales at an address in a kommun: [{date, period, price, sqmPrice, type}] oldest first, or null when it
// couldn't be asked (an older backend, the network): then the card says nothing about the address's sales.
export const fetchAddressSales = (address, kommun) => {
    const street = streetAddress(address);
    if (!street || !kommun) return Promise.resolve([]);
    const key = `${street.toLowerCase()}|${String(kommun).toLowerCase()}`;
    if (salesCache.has(key)) return salesCache.get(key);
    const params = new URLSearchParams({ address: street, kommun });
    const promise = apiFetch(`${API_BASE}/price-history/address?${params}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((body) => (Array.isArray(body?.sales) ? body.sales : null))
        .catch(() => null);
    promise.then((sales) => { if (sales == null) salesCache.delete(key); });
    salesCache.set(key, promise);
    return promise;
};

// The home types a place's price curve can show, with the map markers' colours.
export const PLACE_TYPES = {
    apartment: { sv: 'Lägenheter', en: 'Apartments', colour: '#4a96d0' },
    house: { sv: 'Villor', en: 'Houses', colour: '#1f7a62' },
    'house++': { sv: 'Par/radhus', en: 'Row houses', colour: '#b86b2d' },
    fritidshus: { sv: 'Fritidshus', en: 'Holiday homes', colour: '#b23a2e' },
};

export const typeLabel = (type, swedish) => (PLACE_TYPES[type] || PLACE_TYPES.apartment)[swedish ? 'sv' : 'en'];

const formatKr = (value, swedish) => `${Math.round(value).toLocaleString(swedish ? 'sv-SE' : 'en-US').replace(/\s/g, NBSP)}${NBSP}kr`;

// One line about the sales of one type at the address: flats in a building ("3 sålda i huset, senast 148 400
// kr/m² (2022)") or a house ("Såld 2 gånger, senast 2022 för 94 200 kr/m²").
export const salesCaption = (sales, type, swedish) => {
    const own = (sales || []).filter((sale) => sale.type === type && sale.sqmPrice > 0);
    if (!own.length) return '';
    const last = own[own.length - 1];
    const year = String(last.date || last.period || '').slice(0, 4);
    const sqm = `${formatKr(last.sqmPrice, swedish)}/m²`;
    if (type === 'apartment') {
        if (swedish) return own.length === 1 ? `1 såld i huset, ${sqm} (${year})` : `${own.length} sålda i huset, senast ${sqm} (${year})`;
        return own.length === 1 ? `1 sold in the building, ${sqm} (${year})` : `${own.length} sold in the building, last ${sqm} (${year})`;
    }
    if (swedish) return own.length === 1 ? `Såld ${year} för ${sqm}` : `Såld ${own.length} gånger, senast ${year} för ${sqm}`;
    return own.length === 1 ? `Sold ${year} for ${sqm}` : `Sold ${own.length} times, last in ${year} for ${sqm}`;
};

// All the address's sales in one line, for the area page's strip ("3 sålda på adressen, senast 148 400 kr/m²
// (2022)"), or ''.
export const addressSalesLine = (sales, swedish) => {
    const priced = (sales || []).filter((sale) => sale.sqmPrice > 0);
    if (!sales?.length) return '';
    const count = swedish
        ? `${sales.length === 1 ? '1 såld' : `${sales.length} sålda`} på adressen`
        : `${sales.length} sold at the address`;
    if (!priced.length) return count;
    const last = priced[priced.length - 1];
    const year = String(last.date || last.period || '').slice(0, 4);
    return `${count}, ${swedish ? 'senast' : 'last'} ${formatKr(last.sqmPrice, swedish)}/m² (${year})`;
};

// The genitive of a Swedish name: "Stockholms", but "Borås".
const genitive = (name) => (/[sxz]$/i.test(name) ? name : `${name}s`);

// Why a curve isn't the area's own (too few sales of the type there): it follows the kommun, the län or Sweden.
export const curveLevelNote = (level, type, kommun, swedish) => {
    if (!level || level === 'regso') return '';
    const what = typeLabel(type, swedish).toLowerCase();
    if (swedish) {
        const whose = level === 'kommun' && kommun ? `${genitive(kommun)} ${what}` : (level === 'lan' ? `länets ${what}` : `${what} i hela landet`);
        return `Få ${what} säljs här, så kurvan följer mest ${whose}.`;
    }
    const whose = level === 'kommun' && kommun ? `${kommun}'s ${what}` : (level === 'lan' ? `the county's ${what}` : `${what} across Sweden`);
    return `Few ${what} are sold here, so the curve mostly follows ${whose}.`;
};
